<template>
  <v-container class="mt-3" :class="isMobile ? 'pa-0' : ''">
    <v-alert v-if="errorMsg" type="error" outlined>{{ errorMsg }}</v-alert>
    <v-card v-if="!errorMsg">
      <v-card-title
        class="text-h4 text-no-wrap"
        :class="isMobile ? 'pl-1 pr-1' : ''"
      >
        <span class="mb-2">Recruitment process</span>
        <v-spacer></v-spacer>
        <v-img
          :src="curProcess.logo"
          max-width="150"
          class="mb-2 ml-1 mt-3"
        ></v-img>
      </v-card-title>
      <v-card-text :class="isMobile ? 'pl-1 pr-1' : ''">
        <v-row>
          <v-col cols="12" class="mb-2 pb-0">
            <div class="text-body-1">{{ curProcess.description }}</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="mb-2 pb-0">
            <span class="text-h5">Applying</span>
          </v-col>
          <v-col cols="12" class="mt-0 pt-0 mb-2 pb-0 text-body-1">
            First step is to get your resume in the system - there are several
            ways to do this, we list couple of ways below along with their
            effectiveness.
          </v-col>
        </v-row>
        <v-row
          v-for="(item, index) in curProcess.gettingInTheProcess"
          :key="index"
        >
          <v-col cols="12" class="d-flex flex-row text-h6 mb-0 pb-0 mt-1 pt-0">
            {{ item.method }}
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="item.effectiveness"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                :color="impactColor(item.effectiveness)"
              >
              </v-rating>
            </div>
          </v-col>
          <v-col cols="12" class="d-flex justify-space-between mt-0 pt-0">
            <div>{{ item.description }}</div>
          </v-col>
        </v-row>
        <v-divider class="my-4"></v-divider>
        <v-row>
          <v-col cols="12" class="mb-1 pb-0 ml-1">
            <span class="text-h5">Roles</span>
          </v-col>
          <v-col cols="12" class="mt-1 mb-2">
            <v-chip
              v-for="(role, index) in curProcess.roles"
              :key="index"
              :color="selectedRole === role.id ? 'primary' : ''"
              class="mr-2 mb-2"
              @click="selectedRole = role.id"
            >
              {{ role.title }}
            </v-chip>
          </v-col>
          <v-col cols="12" class="text-body-1 mt-0 pt-0">
            {{
              curProcess.roles.find((r) => r.id === selectedRole).description
            }}
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" :class="isMobile ? 'pl-0 ' : ''">
            <v-timeline :dense="isMobile">
              <v-timeline-item
                v-for="(stage, index) in curProcess.roles.find(
                  (r) => r.id === selectedRole
                ).interviewStages"
                :key="index"
                small
                color="primary"
              >
                <span slot="opposite" class="font-weight-medium">{{
                  stage.timeline
                }}</span>
                <v-card>
                  <v-card-title class="text-h6" style="word-break: normal">
                    {{ stage.stage }}
                  </v-card-title>
                  <v-card-subtitle v-if="isMobile">{{
                    stage.timeline
                  }}</v-card-subtitle>
                  <v-card-text>{{ stage.description }}</v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      errorMsg: "",
      selectedCompany: "",
      selectedRole: "",
      curProcess: {},
      processes: [
        {
          companyId: "goog",
          companyName: "Google",
          logo: require("../assets/goog-logo.jpg"),
          description:
            "Google recruitment process is known to be rigorous and thorough, designed to assess both technical skills and cultural fit. The process typically involves multiple stages, including initial screenings, technical interviews, and onsite interviews. Candidates can expect a mix of coding challenges, system design questions, and behavioral assessments.",
          gettingInTheProcess: [
            {
              method: "Get referred by a current employee",
              effectiveness: 3,
              description:
                "Leverage your network to get a referral from someone who works at the company. If you don't know anyone, try reaching out on LinkedIn or find connections online (e.g. through apps like Blind).",
            },
            {
              method: "Contact recruiters on LinkedIn",
              effectiveness: 3,
              description:
                "Reach out to company recruiters on LinkedIn to express your interest.",
            },
            {
              method: "Attend a campus recruitment event",
              effectiveness: 2,
              description:
                "Participate in recruitment events at your university or college.",
            },
            {
              method: "Apply online through the company's career portal",
              effectiveness: 1,
              description:
                "Submit your application through the official company website.",
            },
          ],
          roles: [
            {
              title: "Software Engineer",
              id: "SWE",
              description:
                "Software Engineer is responsible for developing and maintaining software applications.",
              interviewStages: [
                {
                  stage: "Applying and getting sourced",
                  timeline: "few days to few weeks",
                  description:
                    "The recruitment team reviews your application and decides whether to move forward. Google typically moves slowly due to high volume of applications, so be patient. If you get sourced by a recruiter, this step is usually faster.",
                },
                {
                  stage: "Initial recruiter screening",
                  timeline: "1-2 weeks",
                  description:
                    "A brief phone call to assess your basic qualifications and fit for the role. At this step recruiter wants to make sure you meet the minimum requirements.",
                },
                {
                  stage: "Initial technical screening",
                  timeline: "1-2 weeks",
                  description:
                    "A single 1-hour technical interview, usually conducted via video call. You'll be asked to solve coding problems and explain your thought process. This step may be skipped for candidates with more experience or strong referrals.",
                },
                {
                  stage: "Technical Interview",
                  timeline: "2-3 weeks",
                  description:
                    "Usually about 5, 1-hour interviews conducted by different people. This is a more in-depth interview focusing on your technical skills and problem-solving abilities. Depending on the role and level, there may be more focus on system design and architecture rather than just coding. One of the interviews is usually a Googliness interview to assess cultural fit.",
                },
                {
                  stage: "Hiring committee review",
                  timeline: "1-2 weeks",
                  description:
                    "After the interviews, your performance is reviewed by a hiring committee. Hiring committee decides what level to hire you at. They consider feedback from all interviewers and make a decision on whether to extend an offer.",
                },
                {
                  stage: "Optional, additional interviews",
                  timeline: "1-2 weeks",
                  description:
                    "In some cases, you may be asked to do additional interviews if the hiring committee needs more information. This can be a good sign, as it indicates strong interest and a desire to gather more data to support a hire. Hiring committee may also ask for additional interviews if there were mixed feedback from the initial interviews. ",
                },
                {
                  stage: "Team matching",
                  timeline: "few days to few weeks",
                  description:
                    "If you pass the hiring committee review, you are cleared to be hired. The next step is to find a team that is a good fit for you. This can take some time, as it depends on team needs and availability. If you applied to a specific team, this step ios usually skipped.",
                },
              ],
            },
            {
              title: "Engineering Manager",
              id: "EM",
              description:
                "Engineering manager is responsible for managing people and leading engineering teams.",
              interviewStages: [
                {
                  stage: "Applying and getting sourced",
                  timeline: "few days to few weeks",
                  description:
                    "The recruitment team reviews your application and decides whether to move forward. Google typically moves slowly due to high volume of applications, so be patient. If you get sourced by a recruiter, this step is usually faster.",
                },
                {
                  stage: "Initial recruiter screening",
                  timeline: "1-2 weeks",
                  description:
                    "A brief phone call to assess your basic qualifications and fit for the role. At this step recruiter wants to make sure you meet the minimum requirements.",
                },
                {
                  stage: "Initial screening",
                  timeline: "1-2 weeks",
                  description:
                    "A single 1-hour interview, usually conducted via video call. This screening probes technical depth or system design (architecture/tradeoffs), and leadership/people management skills (hiring, performance, conflict, org design etc.). Expect open-ended systems questions and behavioral scenarios. This step may be skipped for candidates with more experience or strong referrals.",
                },
                {
                  stage: "Onsite/virtual interviews",
                  timeline: "2-3 weeks",
                  description:
                    "a multi-panel loop (commonly ~4–6 interviewers) covering a mix such as: deep system design / technical credibility, cross-functional partnership/product judgment, people & hiring/mentoring examples, and sometimes a coding or debugging check depending on level. One of the interviews is usually a Googliness interview to assess cultural fit.",
                },
                {
                  stage: "Hiring committee review",
                  timeline: "1-2 weeks",
                  description:
                    "After the interviews, your performance is reviewed by a hiring committee. Hiring committee decides what level to hire you at. They consider feedback from all interviewers and make a decision on whether to extend an offer.",
                },
                {
                  stage: "Optional, additional interviews",
                  timeline: "1-2 weeks",
                  description:
                    "In some cases, you may be asked to do additional interviews if the hiring committee needs more information. This can be a good sign, as it indicates strong interest and a desire to gather more data to support a hire. Hiring committee may also ask for additional interviews if there were mixed feedback from the initial interviews. ",
                },
                {
                  stage: "Team matching",
                  timeline: "few days to few weeks",
                  description:
                    "If you pass the hiring committee review, you are cleared to be hired. The next step is to find a team that is a good fit for you. This can take some time, as it depends on team needs and availability. If you applied to a specific team, this step ios usually skipped.",
                },
              ],
            },
            {
              title: "Technical Writer",
              id: "TW",
              description:
                "Technical Writer is responsible for creating and maintaining technical documentation, both internal and external.",
              interviewStages: [
                {
                  stage: "Applying and getting sourced",
                  timeline: "few days to few weeks",
                  description:
                    "The recruitment team reviews your application and decides whether to move forward. Google typically moves slowly due to high volume of applications, so be patient. If you get sourced by a recruiter, this step is usually faster.",
                },
                {
                  stage: "Initial recruiter screening",
                  timeline: "1-2 weeks",
                  description:
                    "A brief phone call to assess your basic qualifications and fit for the role. At this step recruiter wants to make sure you meet the minimum requirements.",
                },
                {
                  stage: "Initial technical screening",
                  timeline: "1-2 weeks",
                  description:
                    "A single 1-hour technical interview, usually conducted via video call. You'll be asked to solve coding problems and explain your thought process. This step may be skipped for candidates with more experience or strong referrals.",
                },
                {
                  stage: "Technical Interview",
                  timeline: "2-3 weeks",
                  description:
                    "Usually about 5, 30-45 min interviews conducted by different people including fellow Technical Writers, Software Engineers, and Product Managers. You can be asked to describe your documentation writing process, engaging with stakeholders to gather information etc. Technical Writer is expected to have a mix of technical, organizational and soft skills. One of the interviews is usually a Googliness interview to assess cultural fit.",
                },
                {
                  stage: "Hiring committee review",
                  timeline: "1-2 weeks",
                  description:
                    "After the interviews, your performance is reviewed by a hiring committee. Hiring committee decides what level to hire you at. They consider feedback from all interviewers and make a decision on whether to extend an offer.",
                },
                {
                  stage: "Team matching",
                  timeline: "few days to few weeks",
                  description:
                    "If you pass the hiring committee review, you are cleared to be hired. The next step is to find a team that is a good fit for you. This can take some time, as it depends on team needs and availability. If you applied to a specific team, this step ios usually skipped.",
                },
              ],
            },
          ],
        },
        {
          companyId: "meta",
          companyName: "Meta",
          logo: require("../assets/meta-logo.jpg"),
          description:
            "Meta recruitment process is known to be rigorous and thorough, but also faster than other big corporations (e.g. Google). Meta is very competitive when it comes to compensation, so you can expect slightly better offers. The process typically involves multiple stages, including initial screenings, technical interviews, and onsite interviews. Candidates can expect a mix of coding challenges, system design questions, and behavioral assessments.",
          characteristics: [
            "Faster interview process",
            "",
            "Focus on innovation and cutting-edge technology",
          ],
          gettingInTheProcess: [
            {
              method: "Get referred by a current employee",
              effectiveness: 3,
              description:
                "Leverage your network to get a referral from someone who works at the company. If you don't know anyone, try reaching out on LinkedIn or find connections online (e.g. through apps like Blind).",
            },
            {
              method: "Contact recruiters on LinkedIn",
              effectiveness: 3,
              description:
                "Reach out to company recruiters on LinkedIn to express your interest.",
            },
            {
              method: "Attend a campus recruitment event",
              effectiveness: 2,
              description:
                "Participate in recruitment events at your university or college.",
            },
            {
              method: "Apply online through the company's career portal",
              effectiveness: 1,
              description:
                "Submit your application through the official company website.",
            },
          ],
          roles: [
            {
              title: "Software Engineer",
              id: "SWE",
              description:
                "Software Engineer is responsible for developing and maintaining software applications.",
              interviewStages: [
                {
                  stage: "Applying and getting sourced",
                  timeline: "few days to few weeks",
                  description:
                    "The recruitment team reviews your application and decides whether to move forward. If you get sourced by a recruiter, this step is usually faster.",
                },
                {
                  stage: "Initial recruiter screening",
                  timeline: "1 week",
                  description:
                    "A brief phone call to assess your basic qualifications and fit for the role. At this step recruiter wants to make sure you meet the minimum requirements.",
                },
                {
                  stage: "Initial technical screening",
                  timeline: "1-2 weeks",
                  description:
                    "A single 45-60 min technical interview, usually conducted via video call using dedicated online notebook. You'll be asked to solve coding problems and explain your thought process. This step may be skipped for candidates with more experience or strong referrals.",
                },
                {
                  stage: "Technical Interviews",
                  timeline: "1-2 weeks",
                  description:
                    "Usually about 5, 45-60 min interviews conducted by different people from teams across the company. Depending on the role and level, you can expect 2-3 coding interviews, 1 system design interview and 1 behavioral interview. The higher the level, the more focus on system design and behavioral interviews. Senior levels are usually also tested on leadership skills.",
                },
                {
                  stage: "Team matching and offer",
                  timeline: "few days to few weeks",
                  description:
                    "If there are no concerns from the interviewers, you are cleared to be hired. The next step is to find a team that is a good fit for you. This can take some time, as it depends on team needs and availability. If you applied to a specific team, this step is usually skipped. If you are a good fit for a team, you will receive an offer.",
                },
              ],
            },
            {
              title: "Engineering Manager",
              id: "EM",
              description:
                "Engineering manager is responsible for managing people and leading engineering teams.",
              interviewStages: [
                {
                  stage: "Applying and getting sourced",
                  timeline: "few days to few weeks",
                  description:
                    "The recruitment team reviews your application and decides whether to move forward. If you get sourced by a recruiter, this step is usually faster.",
                },
                {
                  stage: "Initial recruiter screening",
                  timeline: "1 week",
                  description:
                    "A brief phone call to assess your basic qualifications and fit for the role. At this step recruiter wants to make sure you meet the minimum requirements.",
                },
                {
                  stage: "Hiring manager/peer EM screening",
                  timeline: "1-2 weeks",
                  description:
                    "A brief phone call to assess your fit for the role and discuss your experience. You should expect questions about your management style, leadership experience, and how you handle various situations. This step may be skipped for candidates with more experience or strong referrals.",
                },
                {
                  stage: "Technical screening",
                  timeline: "1-2 weeks",
                  description:
                    "Even though it is a manager role, you are expected to be technically credible. Technical screening for engineering managers typically includes system design questions and may involve coding exercises e.g. code review or optimization. You should be prepared to discuss trade-offs and justify your decisions.",
                },
                {
                  stage: "Technical Interviews",
                  timeline: "1-2 weeks",
                  description:
                    "Usually about 5, 45-60 min interviews conducted by different people from teams across the company. You should expect a people management/leadership interview, a technical architecture/system design interviews (usually at least 2) and another coding interview focused on code understanding, debugging and review. The higher the level, the more focus on system design and behavioral interviews.",
                },
                {
                  stage: "Team matching and offer",
                  timeline: "few days to few weeks",
                  description:
                    "If there are no concerns from the interviewers, you are cleared to be hired. The next step is to find a team that is a good fit for you - as an engineering manager you will be connected with several engineering directors and VPs to find the best match. This can take some time, as it depends on team needs and availability. If you applied to a specific team, this step is usually skipped. If you are a good fit for a team, you will receive an offer.",
                },
              ],
            },
          ],
        },
        {
          companyId: "amzn",
          companyName: "Amazon",
          logo: require("../assets/amzn-logo.jpg"),
          description:
            "Amazon recruitment process is relatively faster than some other big corporations (e.g. Google). The process typically involves multiple stages, including initial screenings, technical interviews, and onsite interviews. Candidates can expect a mix of coding challenges, system design questions, and behavioral assessments.",
          characteristics: [
            "Focus on Amazon leadership principles",
            "Strong emphasis on STAR method for behavioral questions",
            "Bar raiser interviewers",
            "Online assessment for most software engineer roles",
          ],
          gettingInTheProcess: [
            {
              method: "Get referred by a current employee",
              effectiveness: 3,
              description:
                "Leverage your network to get a referral from someone who works at the company. If you don't know anyone, try reaching out on LinkedIn or find connections online (e.g. through apps like Blind).",
            },
            {
              method: "Contact recruiters on LinkedIn",
              effectiveness: 3,
              description:
                "Reach out to company recruiters on LinkedIn to express your interest.",
            },
            {
              method: "Attend a campus recruitment event",
              effectiveness: 2,
              description:
                "Participate in recruitment events at your university or college.",
            },
            {
              method: "Apply online through the company's career portal",
              effectiveness: 1,
              description:
                "Submit your application through the official company website.",
            },
          ],
          roles: [
            {
              title: "Software Engineer",
              id: "SWE",
              description:
                "Software Engineer is responsible for developing and maintaining software applications.",
              interviewStages: [
                {
                  stage: "Applying and getting sourced",
                  timeline: "few days to few weeks",
                  description:
                    "The recruitment team reviews your application and decides whether to move forward. If you get sourced by a recruiter, this step is usually faster.",
                },
                {
                  stage: "Initial recruiter screening",
                  timeline: "1 week",
                  description:
                    "In most cases Amazon recruits for a specific role, so recruiter will inform you about the details. A brief phone call to assess your basic qualifications and fit for the role. At this step recruiter wants to make sure you meet the minimum requirements and that you are familiar with Amazon leadership principles.",
                },
                {
                  stage: "Online assessment",
                  timeline: "couple of days",
                  description:
                    "Most of the software engineer roles require you to complete an online assessment. This is a timed coding test, usually about 90-120 min long. You will be asked to solve 2-3 coding problems using a shared online notebook. The problems are similar to what you would see in a technical interview, but you have more time to think and test your solutions. This step may be skipped for some candidates.",
                },
                {
                  stage: "Technical screening",
                  timeline: "1-2 weeks",
                  description:
                    "Typically 1–2 rounds, 45–60 minutes each, conducted via phone or video call. You will be asked to solve coding problems and explain your thought process. You should also expect behavioral questions focused on Amazon leadership principles.",
                },
                {
                  stage: "On-site/virtual technical loop",
                  timeline: "2-3 weeks",
                  description:
                    "4-6 interviews, 45-60 min each, usually all in one day. You will be asked to solve coding problems, design systems, and discuss your past experiences. At least one interviewer is a specially trained `bar raiser` whose job is to ensure high hiring standards. You should expect a mix of coding, system design, and behavioral questions focused on Amazon leadership principles.",
                },
                {
                  stage: "Debrief and offer",
                  timeline: "few days to few weeks",
                  description:
                    "If there are no concerns from the interviewers, you are cleared to be hired. Since the interviews are for a specific role and team, you will usually receive an offer shortly after the interviews.",
                },
              ],
            },
            {
              title: "Engineering Manager",
              id: "EM",
              description:
                "Engineering manager is responsible for managing people and leading engineering teams.",
              interviewStages: [
                {
                  stage: "Applying and getting sourced",
                  timeline: "few days to few weeks",
                  description:
                    "The recruitment team reviews your application and decides whether to move forward. If you get sourced by a recruiter, this step is usually faster.",
                },
                {
                  stage: "Initial recruiter screening",
                  timeline: "1 week",
                  description:
                    "Recruiter will outline the interview stages and may ask about your management experience, team size, and types of systems you’ve led. Recruiter will also assess your familiarity with Amazon leadership principles.",
                },
                {
                  stage: "Phone/video screening",
                  timeline: "1-2 weeks",
                  description:
                    "Typically 2 rounds, 45–60 minutes each, conducted via phone or video call. One round will be a technical deep-dive, it could be a live-coding or architectural design discussion. It will focus on high-level system design, scaling considerations, and ability to guide technical decisions—not just writing code. The other round will focus on leadership and behavioral questions, with heavy emphasis on Amazon leadership principles and expect a STAR-style responses. You should be prepared to discuss your management style, conflict resolution, hiring practices, and how you’ve led teams to success.",
                },
                {
                  stage: "Written exercise",
                  timeline: "couple of days",
                  description:
                    "Some EM candidates may be asked to complete a written exercise. This could involve writing a design doc, a project proposal, or a response to a hypothetical management scenario. The goal is to assess your written communication skills and how you approach problem-solving in a leadership context.",
                },
                {
                  stage: "On-site/virtual technical loop",
                  timeline: "2-3 weeks",
                  description:
                    "4-6 interviews, 45-60 min each, usually all in one day. You will be asked about System design / technical architecture (1–2 interviews): Large-scale systems, trade-offs, scaling, operational excellence. People leadership & delivery management (2–3 interviews): Building and leading teams, hiring, performance management, cross-team collaboration. Business/strategy focus: Working with product, setting long-term technical vision. At least one interviewer is a specially trained `bar raiser` whose job is to ensure high hiring standards. You should expect a mix of system design, and behavioral questions focused on Amazon leadership principles.",
                },
                {
                  stage: "Debrief and offer",
                  timeline: "few days to few weeks",
                  description:
                    "If there are no concerns from the interviewers, you are cleared to be hired. Since the interviews are for a specific role and team, you will usually receive an offer shortly after the interviews.",
                },
              ],
            },
          ],
        },
      ],
    };
  },
  computed: {
    isMobile() {
      // Return true if the screen is "xs" or "sm"
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
  },
  mounted() {
    if (this.$route.query.company) {
      this.selectedCompany = this.$route.query.company;
    }
    if (this.selectedCompany === "") {
      this.errorMsg = "Company not specified. Bad url?";
    } else {
      this.curProcess = this.processes.find(
        (p) => p.companyId === this.selectedCompany
      );
      console.log("curProcess: " + JSON.stringify(this.curProcess, null, 2));
      if (this.curProcess) {
        console.log("looking for role: " + this.$route.query.role);
        var role = this.curProcess.roles.find(
          (r) => r.id === this.$route.query.role
        );
        console.log("role from url: " + JSON.stringify(role, null, 2));
        if (!role) {
          this.selectedRole = this.curProcess.roles[0].id;
        } else {
          this.selectedRole = role.id;
        }
      } else {
        this.errorMsg = "Unknown company: " + this.selectedCompany;
      }
    }
  },
  methods: {
    impactColor(score) {
      if (score == 3) return "red darken-2";
      if (score == 2) return "orange darken-2";
      return "yellow darken-2";
    },
  },
};
</script>
